import { Component, Input, OnChanges, TemplateRef, inject } from '@angular/core';

import { BetSlipType } from '@bpos';
import { GameType } from '@bpos/v1/sports-promo';
import { CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { OddsFormatterService } from '@frontend/sports/odds/feature';
import { Cards } from '@frontend/sports/types/components/sitecore';
import { UserOddsFormatSettingsService, UserService } from '@frontend/sports/user/feature';
import { IntlService } from '@frontend/vanilla/core';

import { PayoutInfoLocation, TokensSummaryService } from '../../../../../../lib/tokens-base/tokens-summary.service';
import { IRewardToken } from '../../../reward-tokens.model';
import { isBetBuilderExclusiveToken, isBetBuilderToken, mapOdds } from '../../../services/reward-tokens.utils';
import { RewardTokenLimitation } from './reward-token-limitations.component';

@Component({
    template: '',
    standalone: false,
})
export abstract class RewardTokenCardBaseComponent<T extends IRewardToken> implements OnChanges {
    @Input() token: T;
    @Input() isRewardBannerView = false;
    termsAndConditionLinkText = '';
    @Input() isRewardDrawerEnabled: boolean;
    @Input() isTokenSelected = false;
    @Input() vipBadgeTemplate?: TemplateRef<any>;
    showPayout: boolean;
    tokenCards: Cards;
    applyOrRemoveTokenText = '';

    protected readonly userOddsFormatSettingsService = inject(UserOddsFormatSettingsService);

    constructor(
        public sitecore: Sitecore,
        protected crmConfig: CrmConfig,
        protected user: UserService,
        protected intlService: IntlService,
        protected oddsFormatterService: OddsFormatterService,
        protected tokensPayoutInfo: TokensSummaryService,
    ) {
        this.tokenCards = this.sitecore.crmTokens.cards;
    }

    ngOnChanges(): void {
        this.applyOrRemoveTokenText = this.isTokenSelected ? this.sitecore.crmTokens.messages.Remove : this.sitecore.crmTokens.messages.Apply;
        this.showPayout = this.showPayoutInfo(PayoutInfoLocation.Drawer);
    }

    getTokenLimitations(): RewardTokenLimitation[] {
        const result: RewardTokenLimitation[] = [];
        const isSingle = this.token.filter.betSlipType?.toString() === BetSlipType.Single;
        // new implementation is not required here, need to use parent level in expire & bet-type
        if (!this.isRewardDrawerEnabled) {
            const slipType = this.getBetTypeDetails();
            if (slipType) {
                result.push({
                    name: this.tokenCards.BetType,
                    value: slipType,
                });
            }
        }

        if (this.token.minimumLegs || this.token.filter?.betSlipType === BetSlipType.Combo) {
            result.push({
                name: this.tokenCards.TokenMinLegs,
                value: this.token.minimumLegs ? this.token.minimumLegs.toString() : '2',
            });
        }

        if (!isSingle && this.token.selectionLevelMinimumOdds) {
            result.push({
                name: this.tokenCards.SelectionLevelMinimumOdds,
                value: this.oddsFormatterService.format(mapOdds(this.token.selectionLevelMinimumOdds), {
                    displayFormat: this.userOddsFormatSettingsService.oddsFormat,
                }),
            });
        }

        if (this.token.minimumOdds) {
            result.push({
                name: isSingle ? this.tokenCards.MinOddsForSingle : this.tokenCards.MinOddsForCombo,
                value: this.oddsFormatterService.format(mapOdds(this.token.minimumOdds), {
                    displayFormat: this.userOddsFormatSettingsService.oddsFormat,
                    cutZero: true,
                }),
            });
        }

        if (!isSingle && this.token.selectionLevelMaximumOdds) {
            result.push({
                name: this.tokenCards.SelectionLevelMaximumOdds,
                value: this.oddsFormatterService.format(mapOdds(this.token.selectionLevelMaximumOdds), {
                    displayFormat: this.userOddsFormatSettingsService.oddsFormat,
                    cutZero: true,
                }),
            });
        }

        if (this.token.maximumOdds) {
            result.push({
                name: isSingle ? this.tokenCards.MaxOddsForSingle : this.tokenCards.MaxOddsForCombo,
                value: this.oddsFormatterService.format(mapOdds(this.token.maximumOdds), {
                    displayFormat: this.userOddsFormatSettingsService.oddsFormat,
                    cutZero: true,
                }),
            });
        }

        return result;
    }

    getTokenUserExpire(): RewardTokenLimitation[] {
        return [
            {
                name: this.tokenCards.TokenExpires,
                value: this.intlService.formatDate(this.token.expiryDate, 'short'),
            },
        ];
    }

    getTokenBetType(): RewardTokenLimitation[] {
        const slipType = this.getBetTypeDetails();
        if (slipType) {
            return [
                {
                    name: this.tokenCards.BetType,
                    value: slipType,
                },
            ];
        }

        return [];
    }

    // If any changes are made related to the bet type, applicable changes need to be made in the RewardTokensCardDetailsService file also.
    private getBetTypeDetails(): string {
        let slipType = '';

        if (isBetBuilderExclusiveToken(this.token)) {
            slipType = this.sitecore.crmTokens.cards.BuildABet;
        } else if (this.token.filter.gameType && !this.token.filter.betSlipType) {
            slipType = this.getGameTypeName(this.token.filter.gameType);
        } else if (this.token.filter.gameType && this.token.filter.betSlipType) {
            slipType = this.getBetTypeAndGameTypeName(this.token.filter.betSlipType, this.token.filter.gameType);
        } else {
            slipType = this.sitecore.crmTokens.cards.Any;
        }

        return slipType;
    }

    private getGameTypeName(gameType: GameType): string {
        let slipType = '';
        switch (gameType) {
            case GameType.InPlay:
                slipType = this.tokenCards.LiveSingleMultiple;
                break;
            case GameType.PreMatch:
                slipType = isBetBuilderToken(this.token)
                    ? this.token.isSingleAndComboSelected
                        ? this.tokenCards.Prematch
                        : this.tokenCards.BuildABet
                    : this.tokenCards.PrematchSingleMultiple;
                break;
            case GameType.Mixed:
                slipType = isBetBuilderToken(this.token)
                    ? this.crmConfig.tokens.isQuickViewEnabled
                        ? this.sitecore.crmTokens.cards.BuildABetAny
                        : this.sitecore.crmTokens.cards.Any
                    : this.sitecore.crmTokens.cards.SingleMultiple;
                break;
        }

        return slipType;
    }

    private getBetTypeAndGameTypeName(betSlipType: BetSlipType, gameType: GameType): string {
        let slipType = '';
        if (gameType === GameType.PreMatch && !betSlipType) {
            slipType = isBetBuilderToken(this.token) ? this.sitecore.crmTokens.cards.BuildABet : this.sitecore.crmTokens.cards.PrematchSingleMultiple;
        } else if (gameType === GameType.PreMatch && betSlipType === BetSlipType.Single) {
            slipType = isBetBuilderToken(this.token) ? this.sitecore.crmTokens.cards.PrematchSingleBAB : this.sitecore.crmTokens.cards.PrematchSingle;
        } else if (gameType === GameType.PreMatch && betSlipType === BetSlipType.Combo) {
            slipType = isBetBuilderToken(this.token)
                ? this.sitecore.crmTokens.cards.PrematchMultipleBAB
                : this.sitecore.crmTokens.cards.PrematchMultiple;
        } else if (gameType === GameType.InPlay && betSlipType === BetSlipType.Single) {
            slipType = slipType = this.tokenCards.LiveSingle;
        } else if (gameType === GameType.InPlay && betSlipType === BetSlipType.Combo) {
            slipType = this.tokenCards.LiveMultiple;
        } else if (gameType === GameType.Mixed && this.token.filter.betSlipType === BetSlipType.Single) {
            slipType = slipType = isBetBuilderToken(this.token) ? this.sitecore.crmTokens.cards.SingleBAB : this.sitecore.crmTokens.cards.Single;
        } else if (gameType === GameType.Mixed && betSlipType === BetSlipType.Combo) {
            slipType = isBetBuilderToken(this.token) ? this.sitecore.crmTokens.cards.MultipleBAB : this.sitecore.crmTokens.cards.Multiple;
        }

        return slipType;
    }

    applyRemoveButtonsShowHide(): boolean {
        return this.crmConfig.tokens.showApplyRemoveButtons && !this.isRewardBannerView;
    }

    private showPayoutInfo(payoutInfoLocation: PayoutInfoLocation): boolean {
        return this.tokensPayoutInfo.isPayoutInfoEnabled(payoutInfoLocation, this.token.rewardTokenType, this.token.payoutType);
    }
}
